import React, { useState, useEffect } from 'react';
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    TextField,
    Button,
    Avatar,
    IconButton,
    Divider,
    Alert,
    Snackbar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    ListItemIcon,
    Chip,
    Tabs,
    Tab,
    Toolbar
} from '@mui/material';
import {
    PhotoCamera,
    Edit,
    Security,
    Payment,
    Logout,
    Person,
    Email,
    VpnKey,
    CreditCard,
    AccountBalance,
    CheckCircle,
    Cancel,
    Receipt,
    Download,
    History,
    Upgrade,
    AutoRenew,
    Warning,
    Info,
    Business,
    LocationOn,
    Phone
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import useAuth from '../hooks/useAuth';
import useAxiosPrivate from '../hooks/useAxiosPrivate';
import axios from '../api/axios';
import { formatDate } from '../utils/dateUtils';
import { getImageUrl, handleImageError } from '../utils/imageUtils';

const AccountPage = () => {
    const { t } = useTranslation('global');
    const navigate = useNavigate();
    const { auth, setAuth } = useAuth();
    const axiosPrivate = useAxiosPrivate();
    
    const [activeTab, setActiveTab] = useState(0);
    const [userData, setUserData] = useState({
        username: '',
        email: '',
        avatar: '',
        user_id: '',
        full_name: '',
        phone: '',
        preferences: {},
        
        // Dati aziendali dal SaaS DB
        customerCode: '',
        company_name: '',
        company_email: '',
        vat_number: '',
        fiscal_code: '',
        sdi_code: '',
        company_phone: '',
        address: '',
        city: '',
        postal_code: '',
        country: 'IT',
        contact_person: ''
    });
    const [editMode, setEditMode] = useState(false);
    const [tempUserData, setTempUserData] = useState({});
    const [passwordData, setPasswordData] = useState({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
    });
    const [selectedImage, setSelectedImage] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const [success, setSuccess] = useState('');
    const [error, setError] = useState('');
    const [logoutDialog, setLogoutDialog] = useState(false);
    const [loadingPricing, setLoadingPricing] = useState(true);

    // Dati completi per abbonamenti e fatturazione (da sostituire con API reali)
    const [subscriptionData, setSubscriptionData] = useState({
        // Piano attuale
        plan: {
            id: 'professional',
            name: 'Professionale',
            price: 79.00,
            currency: 'EUR',
            billing_cycle: 'monthly',
            trial_days_left: 0,
            features: [
                'Fino a 100 ospiti',
                '10 utenti',
                'Tutte le funzionalità',
                'Supporto prioritario',
                'Report avanzati',
                'App mobile'
            ]
        },
        // Status abbonamento
        subscription: {
            id: 'sub_1234567890',
            status: 'active',
            created_date: '2024-01-15',
            current_period_start: '2025-10-01',
            current_period_end: '2025-11-01',
            cancel_at_period_end: false,
            auto_renew: true,
            next_billing_date: '2025-11-01',
            days_until_renewal: 7
        },
        // Metodo di pagamento
        payment_method: {
            type: 'card',
            card: {
                brand: 'visa',
                last4: '4242',
                exp_month: 12,
                exp_year: 2027,
                name: 'Demo User'
            },
            billing_address: {
                line1: 'Via Roma 123',
                line2: 'Interno 4',
                city: 'Milano',
                state: 'MI',
                postal_code: '20121',
                country: 'IT'
            }
        },
        // Fatturazione
        billing: {
            company_name: 'Casa di Riposo Demo',
            vat_number: 'IT12345678901',
            fiscal_code: 'DMOUSR80A01F205X',
            sdi_code: 'ABCDEFG',
            pec_email: 'fatture@casariposo.demo.it',
            billing_email: 'admin@casariposo.demo.it'
        },
        // Fatture recenti
        recent_invoices: [
            {
                id: 'inv_001',
                number: '2025-001',
                date: '2025-10-01',
                amount: 79.00,
                currency: 'EUR',
                status: 'paid',
                description: 'Professionale - Ottobre 2025',
                pdf_url: '/invoices/2025-001.pdf'
            },
            {
                id: 'inv_002',
                number: '2025-002',
                date: '2025-09-01',
                amount: 79.00,
                currency: 'EUR',
                status: 'paid',
                description: 'Professionale - Settembre 2025',
                pdf_url: '/invoices/2025-002.pdf'
            },
            {
                id: 'inv_003',
                number: '2025-003',
                date: '2025-08-01',
                amount: 79.00,
                currency: 'EUR',
                status: 'paid',
                description: 'Professionale - Agosto 2025',
                pdf_url: '/invoices/2025-003.pdf'
            }
        ],
        // Piani disponibili (caricati dinamicamente dal database)
        available_plans: [],
        // Usage statistics
        usage: {
            elderly_count: 127,
            storage_used_gb: 15.7,
            storage_limit_gb: 100,
            api_calls_month: 2847,
            api_calls_limit: 10000
        }
    });

    useEffect(() => {
        if (!auth?.accessToken) {
            return;
        }
        fetchUserData();
        loadPricingPlans();
    }, [auth?.accessToken]);

    // Carica i piani dal database
    const loadPricingPlans = async () => {
        try {
            setLoadingPricing(true);
            const response = await axios.get('/api/pricing');
            
            // Trasforma i dati dal database nel formato per AccountPage
            const formattedPlans = response.data.map(plan => {
                // Determina il max_elderly in base al piano
                let maxElderly = -1; // default illimitato
                if (plan.plan_name === 'basic') {
                    maxElderly = 20;
                } else if (plan.plan_name === 'professional') {
                    maxElderly = 100;
                }

                return {
                    id: plan.plan_name,
                    name: plan.display_name,
                    price: parseFloat(plan.monthly_price),
                    yearlyPrice: parseFloat(plan.yearly_price),
                    currency: 'EUR',
                    billing_cycle: 'monthly',
                    max_elderly: maxElderly,
                    features: plan.features || [],
                    is_popular: plan.is_popular
                };
            });

            setSubscriptionData(prev => ({
                ...prev,
                available_plans: formattedPlans
            }));

            console.log('✅ Piani prezzi caricati dal database:', formattedPlans);
        } catch (error) {
            console.error('❌ Errore caricamento prezzi:', error);
            // Fallback: usa piani di base se l'API fallisce
            setSubscriptionData(prev => ({
                ...prev,
                available_plans: [
                    {
                        id: 'basic',
                        name: 'Base',
                        price: 29.00,
                        yearlyPrice: 290.00,
                        currency: 'EUR',
                        billing_cycle: 'monthly',
                        max_elderly: 20,
                        features: ['Fino a 20 ospiti', '2 utenti', 'Funzionalità base', 'Supporto email']
                    },
                    {
                        id: 'professional',
                        name: 'Professionale',
                        price: 79.00,
                        yearlyPrice: 790.00,
                        currency: 'EUR',
                        billing_cycle: 'monthly',
                        max_elderly: 100,
                        features: ['Fino a 100 ospiti', '10 utenti', 'Tutte le funzionalità', 'Supporto prioritario']
                    },
                    {
                        id: 'enterprise',
                        name: 'Enterprise',
                        price: 199.00,
                        yearlyPrice: 1990.00,
                        currency: 'EUR',
                        billing_cycle: 'monthly',
                        max_elderly: -1,
                        features: ['Ospiti illimitati', 'Utenti illimitati', 'Supporto 24/7', 'API dedicata']
                    }
                ]
            }));
        } finally {
            setLoadingPricing(false);
        }
    };

    const fetchUserData = async () => {
        try {
            const response = await axiosPrivate.get('/auth/profile');
            console.log('📥 Dati profilo ricevuti:', response.data);
            setUserData(response.data);
            setTempUserData(response.data);
        } catch (error) {
            console.error('❌ Errore nel caricamento del profilo:', error);
            setError(t('account.messages.profileLoadError', 'Errore nel caricamento del profilo utente'));
        }
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleInputChange = (field, value) => {
        setTempUserData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handlePasswordChange = (field, value) => {
        setPasswordData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleImageSelect = (event) => {
        const file = event.target.files[0];
        if (file) {
            setSelectedImage(file);
            const url = URL.createObjectURL(file);
            setPreviewUrl(url);
        }
    };

    const handleSaveProfile = async () => {
        try {
            console.log('💾 Salvataggio profilo con dati:', tempUserData);
            console.log('📸 Avatar selezionato:', selectedImage ? 'SI' : 'NO');
            
            // Invia solo i campi che sono stati effettivamente modificati
            const changedData = {};
            Object.keys(tempUserData).forEach(key => {
                if (tempUserData[key] !== userData[key]) {
                    changedData[key] = tempUserData[key];
                }
            });
            
            console.log('📤 Campi modificati da inviare:', changedData);
            
            // Aggiorna dati profilo (phone va in users, company_phone va in customers)
            const profileResponse = await axiosPrivate.patch('/auth/profile', changedData);
            
            console.log('✅ Profilo salvato, risposta:', profileResponse.data);
            
            // Upload avatar se selezionato
            if (selectedImage) {
                console.log('📤 Inizio upload avatar...');
                const formData = new FormData();
                formData.append('avatar', selectedImage);
                console.log('📦 FormData creato, size:', selectedImage.size, 'bytes');
                
                const avatarResponse = await axiosPrivate.post('/auth/upload-avatar', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data'
                    }
                });
                
                console.log('✅ Avatar caricato, risposta:', avatarResponse.data);
                
                // Ricarica i dati completi dal server per aggiornare l'avatar
                await fetchUserData();
            } else {
                console.log('ℹ️ Nessun avatar da caricare, aggiornamento solo profilo');
                setUserData(tempUserData);
            }
            setEditMode(false);
            setSelectedImage(null);
            setPreviewUrl(null);
            setSuccess(t('account.messages.profileUpdated', 'Profilo aggiornato con successo'));
        } catch (error) {
            console.error('❌ Errore aggiornamento profilo completo:', error);
            console.error('Response data:', error.response?.data);
            console.error('Response status:', error.response?.status);
            console.error('Request config:', error.config);
            setError(t('account.messages.profileUpdateError', 'Errore durante l\'aggiornamento del profilo') + ': ' + (error.response?.data?.error || error.message));
        }
    };

    const handleChangePassword = async () => {
        if (passwordData.newPassword !== passwordData.confirmPassword) {
            setError(t('account.messages.passwordMismatch', 'Le password non coincidono'));
            return;
        }
        
        try {
            await axiosPrivate.patch('/auth/password', {
                currentPassword: passwordData.currentPassword,
                newPassword: passwordData.newPassword
            });
            
            setPasswordData({
                currentPassword: '',
                newPassword: '',
                confirmPassword: ''
            });
            setSuccess(t('account.messages.passwordUpdated', 'Password aggiornata con successo'));
        } catch (error) {
            console.error('Errore cambio password:', error);
            setError(t('account.messages.passwordUpdateError', 'Errore durante il cambio password'));
        }
    };

        const handleLogout = async () => {
        try {
            await axios.post('/auth/logout', {}, {
                withCredentials: true
            });
            setAuth({});
            navigate('/login');
        } catch (error) {
            console.error('Errore durante il logout:', error);
        }
        setLogoutDialog(false);
    };

    // Funzioni per gestione abbonamenti
    const handleChangePlan = (planId) => {
        // TODO: Implementare chiamata API per cambio piano
        setSuccess(t('account.messages.planUpdated', 'Piano aggiornato con successo'));
    };

    const handleCancelSubscription = () => {
        // TODO: Implementare chiamata API per cancellazione
        setSuccess(t('account.messages.subscriptionCancelled', 'Abbonamento cancellato. Rimarrà attivo fino alla fine del periodo di fatturazione.'));
    };

    const handleUpdatePaymentMethod = () => {
        // TODO: Implementare integrazione con Stripe/PayPal
        setSuccess(t('account.messages.paymentMethodUpdated', 'Metodo di pagamento aggiornato con successo'));
    };

    const handleDownloadInvoice = (invoiceId) => {
        // TODO: Implementare download PDF fattura
        window.open(`/api/invoices/${invoiceId}/download`, '_blank');
    };

    const formatCurrency = (amount, currency = 'EUR') => {
        return new Intl.NumberFormat('it-IT', {
            style: 'currency',
            currency: currency
        }).format(amount);
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return 'success';
            case 'paid': return 'success';
            case 'pending': return 'warning';
            case 'cancelled': return 'error';
            case 'overdue': return 'error';
            default: return 'default';
        }
    };

    const getStatusText = (status) => {
        switch (status) {
            case 'active': return 'Attivo';
            case 'paid': return 'Pagata';
            case 'pending': return 'In attesa';
            case 'cancelled': return 'Cancellato';
            case 'overdue': return 'Scaduto';
            default: return status;
        }
    };

        const getAvatarUrl = () => {
        if (previewUrl) return previewUrl;
        // Usa helper multi-tenant per avatar utente
        // Aggiungi timestamp per forzare il reload dell'immagine dopo upload
        const avatarUrl = getImageUrl(userData.avatar);
        if (avatarUrl && userData.avatar) {
            const timestamp = new Date().getTime();
            return `${avatarUrl}?t=${timestamp}`;
        }
        return avatarUrl;
    };

    const renderProfileTab = () => (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Person />
                    Informazioni Profilo
                </Typography>
                
                <Grid container spacing={3}>
                    {/* Avatar Section */}
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                        <div style={{ position: 'relative' }}>
                            <Avatar
                                src={getAvatarUrl()}
                                sx={{ width: 120, height: 120 }}
                                alt="Profile"
                                onError={handleImageError}
                            >
                                {userData.username ? userData.username.charAt(0).toUpperCase() : 'U'}
                            </Avatar>
                            {editMode && (
                                <>
                                    <input
                                        accept="image/*"
                                        style={{ display: 'none' }}
                                        id="avatar-upload"
                                        type="file"
                                        onChange={handleImageSelect}
                                    />
                                    <label htmlFor="avatar-upload">
                                        <IconButton
                                            color="primary"
                                            component="span"
                                            sx={{
                                                position: 'absolute',
                                                bottom: 0,
                                                right: 0,
                                                backgroundColor: 'background.paper',
                                                opacity: 0.95
                                            }}
                                        >
                                            <PhotoCamera />
                                        </IconButton>
                                    </label>
                                </>
                            )}
                        </div>
                    </Grid>

                    {/* Dati Personali */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1, color: 'primary.main' }}>
                            👤 {t('account.sections.personalData', 'Dati Personali')}
                        </Typography>
                    </Grid>
                    
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('account.fields.username', 'Nome Utente')}
                            value={editMode ? tempUserData.username : userData.username}
                            disabled={true}
                            InputProps={{
                                startAdornment: <Person sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('account.fields.email', 'Email Account')}
                            type="email"
                            value={userData.email}
                            disabled={true}
                            helperText={t('account.helperTexts.emailNotEditable', "L'email dell'account non può essere modificata")}
                            InputProps={{
                                startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('account.fields.personalPhone', 'Telefono Personale')}
                            value={editMode ? (tempUserData.phone || '') : (userData.phone || '')}
                            disabled={!editMode}
                            onChange={(e) => handleInputChange('phone', e.target.value)}
                            placeholder="+39 123 456 7890"
                            helperText={t('account.helperTexts.personalPhone', 'Telefono personale dell\'utente')}
                            InputProps={{
                                startAdornment: <Phone sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                    </Grid>

                    {/* Dati Aziendali */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
                            🏢 {t('account.sections.companyData', 'Dati Aziendali')}
                        </Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('account.fields.companyName', 'Ragione Sociale / Nome Azienda')}
                            value={editMode ? (tempUserData.company_name || '') : (userData.company_name || '')}
                            disabled={!editMode}
                            onChange={(e) => handleInputChange('company_name', e.target.value)}
                            InputProps={{
                                startAdornment: <Business sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('account.fields.vatNumber', 'Partita IVA')}
                            value={editMode ? (tempUserData.vat_number || '') : (userData.vat_number || '')}
                            disabled={!editMode}
                            onChange={(e) => handleInputChange('vat_number', e.target.value)}
                            placeholder="IT12345678901"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('account.fields.fiscalCode', 'Codice Fiscale')}
                            value={editMode ? (tempUserData.fiscal_code || '') : (userData.fiscal_code || '')}
                            disabled={!editMode}
                            onChange={(e) => handleInputChange('fiscal_code', e.target.value)}
                            placeholder="RSSMRA80A01H501Z"
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('account.fields.companyEmail', 'Email Aziendale')}
                            type="email"
                            value={editMode ? (tempUserData.company_email || '') : (userData.company_email || '')}
                            disabled={!editMode}
                            onChange={(e) => handleInputChange('company_email', e.target.value)}
                            placeholder="info@azienda.it"
                            helperText={t('account.helperTexts.companyEmail', 'Email che apparirà nelle stampe')}
                            InputProps={{
                                startAdornment: <Email sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('account.fields.companyPhone', 'Telefono Aziendale')}
                            value={editMode ? (tempUserData.company_phone || '') : (userData.company_phone || '')}
                            disabled={!editMode}
                            onChange={(e) => handleInputChange('company_phone', e.target.value)}
                            placeholder="+39 02 1234567"
                            helperText={t('account.helperTexts.companyPhone', 'Telefono della sede legale')}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('account.fields.sdiCode', 'Codice SDI')}
                            value={editMode ? (tempUserData.sdi_code || '') : (userData.sdi_code || '')}
                            disabled={!editMode}
                            onChange={(e) => handleInputChange('sdi_code', e.target.value)}
                            placeholder="ABCDEFG"
                            helperText={t('account.helperTexts.sdiCode', 'Per fatturazione elettronica')}
                            inputProps={{ maxLength: 7 }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('account.fields.contactPerson', 'Persona di Contatto')}
                            value={editMode ? (tempUserData.contact_person || '') : (userData.contact_person || '')}
                            disabled={!editMode}
                            onChange={(e) => handleInputChange('contact_person', e.target.value)}
                            placeholder="Mario Rossi"
                            helperText={t('account.helperTexts.contactPerson', 'Riferimento principale')}
                        />
                    </Grid>

                    {/* Indirizzo AZIENDALE (dal SaaS DB) */}
                    <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1, color: 'primary.main' }}>
                            📍 {t('account.sections.addressData', 'Sede Legale')}
                        </Typography>
                    </Grid>
                    
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('account.fields.address', 'Indirizzo Sede Legale')}
                            value={editMode ? (tempUserData.address || '') : (userData.address || '')}
                            disabled={!editMode}
                            onChange={(e) => handleInputChange('address', e.target.value)}
                            placeholder="Via Roma, 123"
                            InputProps={{
                                startAdornment: <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label={t('account.fields.postalCode', 'CAP')}
                            value={editMode ? (tempUserData.postal_code || '') : (userData.postal_code || '')}
                            disabled={!editMode}
                            onChange={(e) => handleInputChange('postal_code', e.target.value)}
                            placeholder="20121"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label={t('account.fields.city', 'Città')}
                            value={editMode ? (tempUserData.city || '') : (userData.city || '')}
                            disabled={!editMode}
                            onChange={(e) => handleInputChange('city', e.target.value)}
                            placeholder="Milano"
                        />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <TextField
                            fullWidth
                            label={t('account.fields.country', 'Nazione')}
                            value={editMode ? (tempUserData.country || 'IT') : (userData.country || 'IT')}
                            disabled={!editMode}
                            onChange={(e) => handleInputChange('country', e.target.value)}
                            placeholder="IT"
                        />
                    </Grid>

                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                        {editMode ? (
                            <>
                                <Button
                                   
                                    onClick={() => {
                                        setEditMode(false);
                                        setTempUserData(userData);
                                        setSelectedImage(null);
                                        setPreviewUrl(null);
                                    }}
                                >
                                    {t('common.cancel', 'Annulla')}
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleSaveProfile}
                                    startIcon={<Person />}
                                >
                                    {t('common.save', 'Salva')}
                                </Button>
                            </>
                        ) : (
                            <Button
                                variant="contained"
                                startIcon={<Edit />}
                                onClick={() => setEditMode(true)}
                            >
                                {t('account.buttons.edit', 'Modifica')}
                            </Button>
                        )}
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );

    const renderSecurityTab = () => (
        <Card>
            <CardContent>
                <Typography variant="h6" gutterBottom>
                    {t('account.buttons.changePassword', 'Cambia Password')}
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label={t('account.fields.currentPassword', 'Password Attuale')}
                            type="password"
                            value={passwordData.currentPassword}
                            onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                            InputProps={{
                                startAdornment: <VpnKey sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('account.fields.newPassword', 'Nuova Password')}
                            type="password"
                            value={passwordData.newPassword}
                            onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                            InputProps={{
                                startAdornment: <Security sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <TextField
                            fullWidth
                            label={t('account.fields.confirmPassword', 'Conferma Nuova Password')}
                            type="password"
                            value={passwordData.confirmPassword}
                            onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                            InputProps={{
                                startAdornment: <Security sx={{ mr: 1, color: 'text.secondary' }} />
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button
                            variant="contained"
                            onClick={handleChangePassword}
                            disabled={!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                        >
                            {t('account.security.updatePassword', 'Aggiorna Password')}
                        </Button>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );

    const renderSubscriptionTab = () => (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            {/* Piano Attuale */}
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CreditCard />
                        Piano Attuale: {subscriptionData.plan.name}
                    </Typography>
                    
                    <Box sx={{ mb: 3, p: 2, backgroundColor: 'success.light', borderRadius: 1 }}>
                        <Grid container spacing={2} alignItems="center">
                            <Grid item>
                                <CheckCircle color="success" />
                            </Grid>
                            <Grid item xs>
                                <Typography variant="h5">
                                    {formatCurrency(subscriptionData.plan.price)} / mese
                                </Typography>
                                <Typography variant="body2">
                                    {subscriptionData.subscription.auto_renew ? 
                                        `Rinnovo automatico il ${formatDate(subscriptionData.subscription.next_billing_date)}` :
                                        'Rinnovo automatico disabilitato'
                                    }
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Chip 
                                    label={getStatusText(subscriptionData.subscription.status)}
                                    color={getStatusColor(subscriptionData.subscription.status)}
                                    icon={subscriptionData.subscription.status === 'active' ? <CheckCircle /> : <Warning />}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    {/* Alert per rinnovo imminente */}
                    {subscriptionData.subscription.days_until_renewal <= 7 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Il tuo abbonamento si rinnova tra {subscriptionData.subscription.days_until_renewal} giorni
                        </Alert>
                    )}

                    {/* Utilizzo delle risorse */}
                    <Typography variant="subtitle1" gutterBottom sx={{ mt: 2 }}>
                        Utilizzo Risorse
                    </Typography>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                        <Grid item xs={12} md={4}>
                            <Box>
                                <Typography variant="body2">Anziani Gestiti</Typography>
                                <Typography variant="h6">
                                    {subscriptionData.usage.elderly_count}
                                    {subscriptionData.plan.max_elderly !== -1 && ` / ${subscriptionData.plan.max_elderly}`}
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box>
                                <Typography variant="body2">Storage Utilizzato</Typography>
                                <Typography variant="h6">
                                    {subscriptionData.usage.storage_used_gb} GB / {subscriptionData.usage.storage_limit_gb} GB
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box>
                                <Typography variant="body2">Database</Typography>
                                <Typography variant="h6" sx={{ fontSize: '0.9rem', wordBreak: 'break-all' }}>
                                    {auth?.customerDatabase || 'Non disponibile'}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>

                    <Divider sx={{ my: 2 }} />

                    <Grid container spacing={2}>
                        <Grid item>
                            <Button
                               
                                startIcon={<Upgrade />}
                                onClick={() => handleChangePlan('professional')}
                            >
                                Cambia Piano
                            </Button>
                        </Grid>
                        <Grid item>
                            <Button
                               
                                color="error"
                                startIcon={<Cancel />}
                                onClick={handleCancelSubscription}
                            >
                                Cancella Abbonamento
                            </Button>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Metodo di Pagamento */}
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Payment />
                        Metodo di Pagamento
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <CreditCard />
                        <Box>
                            <Typography variant="body1">
                                •••• •••• •••• {subscriptionData.payment_method.card.last4}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {subscriptionData.payment_method.card.brand.toUpperCase()} • 
                                Scade {subscriptionData.payment_method.card.exp_month}/{subscriptionData.payment_method.card.exp_year}
                            </Typography>
                        </Box>
                    </Box>

                    <Typography variant="subtitle2" gutterBottom>Indirizzo di Fatturazione</Typography>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        {userData.company_name && <><strong>{userData.company_name}</strong><br /></>}
                        {userData.address_line1 || 'Nessun indirizzo configurato'}<br />
                        {userData.address_line2 && 
                            <>
                                {userData.address_line2}<br />
                            </>
                        }
                        {userData.postal_code && userData.city && 
                            <>{userData.postal_code} {userData.city} {userData.state && `(${userData.state})`}<br /></>
                        }
                        {userData.country || 'IT'}
                    </Typography>

                    <Button
                       
                        startIcon={<Edit />}
                        onClick={handleUpdatePaymentMethod}
                    >
                        {t('account.subscription.updatePaymentMethod', 'Aggiorna Metodo di Pagamento')}
                    </Button>
                </CardContent>
            </Card>

            {/* Dati Fatturazione */}
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Business />
                        Dati Fatturazione
                    </Typography>

                    {!userData.company_name && !userData.vat_number && (
                        <Alert severity="warning" sx={{ mb: 2 }}>
                            <Typography variant="body2">
                                I dati di fatturazione non sono ancora configurati. 
                                Aggiornali nella sezione <strong>Profilo</strong> per abilitare la fatturazione automatica.
                            </Typography>
                        </Alert>
                    )}

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2">Ragione Sociale</Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {userData.company_name || 'Non configurata'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2">Partita IVA</Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {userData.vat_number || 'Non configurata'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2">Codice Fiscale</Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {userData.fiscal_code || 'Non configurato'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2">Telefono</Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {userData.company_phone || 'Non configurato'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2">Indirizzo</Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {userData.address || 'Non configurato'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2">Città</Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {userData.city ? `${userData.postal_code} ${userData.city}` : 'Non configurata'}
                            </Typography>
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2">Nazione</Typography>
                            <Typography variant="body2" sx={{ mb: 1 }}>
                                {userData.country || 'IT'}
                            </Typography>
                        </Grid>
                    </Grid>

                    <Button
                       
                        startIcon={<Edit />}
                        sx={{ mt: 2 }}
                    >
                        Modifica Dati Fatturazione
                    </Button>
                </CardContent>
            </Card>

            {/* Fatture Recenti */}
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Receipt />
                        Fatture Recenti
                    </Typography>

                    <List>
                        {subscriptionData.recent_invoices.map((invoice) => (
                            <ListItem key={invoice.id} sx={{ 
                                border: '1px solid', 
                                borderColor: 'divider', 
                                borderRadius: 1, 
                                mb: 1 
                            }}>
                                <ListItemIcon>
                                    <Receipt />
                                </ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                            <Typography variant="subtitle1">
                                                Fattura {invoice.number}
                                            </Typography>
                                            <Chip 
                                                label={getStatusText(invoice.status)}
                                                color={getStatusColor(invoice.status)}
                                                size="small"
                                            />
                                        </Box>
                                    }
                                    secondary={
                                        <Box>
                                            <Typography variant="body2">
                                                {formatDate(invoice.date)} • {formatCurrency(invoice.amount)}
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                {invoice.description}
                                            </Typography>
                                        </Box>
                                    }
                                />
                                <IconButton 
                                    onClick={() => handleDownloadInvoice(invoice.id)}
                                    size="small"
                                >
                                    <Download />
                                </IconButton>
                            </ListItem>
                        ))}
                    </List>

                    <Button
                       
                        startIcon={<History />}
                        fullWidth
                        sx={{ mt: 2 }}
                    >
                        Visualizza Tutte le Fatture
                    </Button>
                </CardContent>
            </Card>

            {/* Piani Disponibili */}
            <Card>
                <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Upgrade />
                        Piani Disponibili
                    </Typography>

                    {loadingPricing ? (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="body2" color="text.secondary">
                                Caricamento piani disponibili...
                            </Typography>
                        </Box>
                    ) : (
                        <Grid container spacing={2}>
                            {subscriptionData.available_plans.map((plan) => (
                            <Grid item xs={12} md={4} key={plan.id}>
                                <Card 
                                    variant="outlined"
                                    sx={{ 
                                        height: '100%',
                                        border: plan.id === subscriptionData.plan.id ? '2px solid' : '1px solid',
                                        borderColor: plan.id === subscriptionData.plan.id ? 'primary.main' : 'divider'
                                    }}
                                >
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>
                                            {plan.name}
                                            {plan.id === subscriptionData.plan.id && (
                                                <Chip 
                                                    label="Attuale" 
                                                    color="primary" 
                                                    size="small" 
                                                    sx={{ ml: 1 }}
                                                />
                                            )}
                                        </Typography>
                                        <Typography variant="h4" gutterBottom>
                                            {formatCurrency(plan.price)}
                                            <Typography component="span" variant="body2">
                                                /mese
                                            </Typography>
                                        </Typography>
                                        <Typography variant="body2" gutterBottom>
                                            Max anziani: {plan.max_elderly === -1 ? 'Illimitati' : plan.max_elderly}
                                        </Typography>
                                        <List dense>
                                            {plan.features.map((feature, index) => (
                                                <ListItem key={index} sx={{ px: 0 }}>
                                                    <ListItemIcon sx={{ minWidth: 30 }}>
                                                        <CheckCircle color="success" fontSize="small" />
                                                    </ListItemIcon>
                                                    <ListItemText 
                                                        primary={feature}
                                                        primaryTypographyProps={{ variant: 'body2' }}
                                                    />
                                                </ListItem>
                                            ))}
                                        </List>
                                        {plan.id !== subscriptionData.plan.id && (
                                            <Button
                                               
                                                fullWidth
                                                onClick={() => handleChangePlan(plan.id)}
                                                sx={{ mt: 1 }}
                                            >
                                                {plan.price > subscriptionData.plan.price ? 'Upgrade' : 'Downgrade'}
                                            </Button>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                    )}
                </CardContent>
            </Card>
        </Box>
    );

    return (
        <Box component="main" sx={{ flexGrow: 1, p: 3 }}>
            <Card sx={{ p: 3, mb: 2 }}>
                <Typography variant="h4" fontWeight={600}>
                    Account
                </Typography>
            </Card>

            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                <Tabs value={activeTab} onChange={handleTabChange}>
                    <Tab label="Profilo" />
                    <Tab label="Sicurezza" />
                    <Tab label="Abbonamento" />
                </Tabs>
            </Box>

            {activeTab === 0 && renderProfileTab()}
            {activeTab === 1 && renderSecurityTab()}
            {activeTab === 2 && renderSubscriptionTab()}

            <Box sx={{ mt: 3 }}>
                <Button
                   
                    color="error"
                    startIcon={<Logout />}
                    onClick={() => setLogoutDialog(true)}
                >
                    Logout
                </Button>
            </Box>

            {/* Dialog Logout */}
            <Dialog open={logoutDialog} onClose={() => setLogoutDialog(false)}>
                <DialogTitle>Conferma Logout</DialogTitle>
                <DialogContent>
                    <Typography>
                        Sei sicuro di voler effettuare il logout?
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setLogoutDialog(false)} variant="outlined" color="primary">
                        Annulla
                    </Button>
                    <Button 
                        onClick={handleLogout} 
                        variant="contained"
                        color="error"
                    >
                        Logout
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Snackbars */}
            <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess('')}>
                <Alert onClose={() => setSuccess('')} severity="success">
                    {success}
                </Alert>
            </Snackbar>
            
            <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
                <Alert onClose={() => setError('')} severity="error">
                    {error}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AccountPage;